import type { Commit, Module } from "vuex";

import { OverwriteScope } from "hoxton-shared/enums/overwrites";
import type {
    Editable,
    EditableGroupValue,
    User,
    EditableGroup,
    EditableWithOverwrites,
    Overwrite,
    OverwriteScopes,
    CSSSettings,
    MasterTemplate
} from "hoxton-types";
import type { OpenDcCampaignType } from "hoxton-shared/enums/opendc";

import type { Client, EditableOverwrite, MastheadUploadConfig, StoreCampaign } from "@/types/types";
import type { CreativeXCampaignSettings } from "@/types/preflight/creativeX";
import type { SataliaCampaignSettings } from "@/types/preflight/satalia";

import { dedup, deepClone, formatEditableName, formatGroupName, generateTemplateLabel } from "@/utils";
import { EditableScope } from "@/enums/editables";
import { OverwriteScopeOrder } from "@/enums/overwrites";
import { BannerDefaultDuration } from "@/enums/banners";
import type { LanguageCode } from "@/utils/languages";
import type { RootState } from "@/store/types";

export const storeNamespace = "campaign";

export const CampaignAction = {
    AddEditableGroupValue: `${storeNamespace}/addEditableGroupValue`,
    DisassociateMasterTemplate: `${storeNamespace}/disassociateMasterTemplate`,
    RemoveEditableGroupValue: `${storeNamespace}/removeEditableGroupValue`,
    SetCampaign: `${storeNamespace}/setCampaign`,
    SetDcProfileId: `${storeNamespace}/setDcProfileId`,
    SetEditableOverwrites: `${storeNamespace}/setEditableOverwrites`,
    SetGroups: `${storeNamespace}/setGroups`,
    SetInitialState: `${storeNamespace}/setInitialState`,
    SetIsLocked: `${storeNamespace}/setIsLocked`,
    SetMasterTemplates: `${storeNamespace}/setMasterTemplates`,
    UpdateEditableGroupValue: `${storeNamespace}/updateEditableGroupValue`,
    SetEditableRestriction: `${storeNamespace}/setEditableRestriction`,
    SetCampaignUsers: `${storeNamespace}/setCampaignUsers`
};

export const CampaignGetters = {
    masterTemplateLabels: `${storeNamespace}/masterTemplateLabels`,
    editableOverwriteValue: `${storeNamespace}/editableOverwriteValue`,
    groupByGroupValue: `${storeNamespace}/groupByGroupValue`,
    groupByGroupValueId: `${storeNamespace}/groupByGroupValueId`,
    groupValueNamesByGroup: `${storeNamespace}/groupValueNamesByGroup`,
    groupEditables: `${storeNamespace}/groupEditables`,
    masterTemplatesBySize: `${storeNamespace}/masterTemplatesBySize`,
    templateEditables: `${storeNamespace}/templateEditables`,
    templateById: `${storeNamespace}/templateById`
};

export const CampaignMutation = {
    AddEditableGroupValueToGroup: "addEditableGroupValueToGroup",
    ClearGroups: "clearGroups",
    RemoveEditableGroupValue: "removeEditableGroupValue",
    RemoveGroupValueFromEditableGroup: "removeGroupValueFromEditableGroup",
    RemoveMasterTemplate: "removeMasterTemplate",
    SetCampaign: "setCampaign",
    SetDcProfileId: "setDcProfileId",
    SetEditable: "setEditable",
    SetEditableGroup: "setEditableGroup",
    SetEditableGroupValue: "setEditableGroupValue",
    SetEditableOverwrite: "setEditableOverwrite",
    SetEditableOverwrites: "setEditableOverwrites",
    SetGroups: "setGroups",
    SetInitialState: "setInitialState",
    SetIsLocked: "setIsLocked",
    SetMasterTemplate: "setMasterTemplate",
    SetMasterTemplates: "setMasterTemplates",
    SetEditableRestriction: "setEditableRestriction",
    SetCampaignUsers: "setCampaignUsers",
    SortEditableGroupValuesInGroup: "sortEditableGroupValuesInGroup"
};

export const campaignDefaultLanguage = "en";

type NormalizedEditableGroup = {
    name: string;
    value: string;
    editableGroupValues: string[];
    editables: string[];
};

type NormalizedState = {
    editables: Record<string, Editable>;
    editableIds: string[];
    editableGroups: Record<string, NormalizedEditableGroup>;
    editableGroupIds: string[];
    editableOverwrites: Record<string, { editableId: string; overwrites: (Overwrite & { editableId: string })[] }>;
    masterTemplates: Record<string, MasterTemplate>;
    masterTemplateIds: string[];
    overwrites: (Overwrite & { editableId: string })[];
    users: Record<string, User>;
    userIds: string[];
};

export type State = {
    client: Client;
    editableGroupValues: Record<string, EditableGroupValue>;
    editableGroupValueIds: string[];
    editableOverwrites: EditableWithOverwrites[];
    groups: (EditableGroup & { label: string })[];
    id: string | null;
    _id: string | null;
    isLocked: boolean;
    languages: LanguageCode[];
    masterTemplates: MasterTemplate[];
    mastheadImageUrl: string | null;
    mastheadUploadConfig: MastheadUploadConfig | null;
    name: string | null;
    jiraTicketUrl: string | null;
    dcProfileId: number | null;
    toneOfVoice: string | null;
    defaultTimelineLength: number;
    groupFeedMappings: {
        group: string;
        feedUrl: string;
        primaryKey: string;
    }[];
    normalized: NormalizedState;
    creativeXPlatformSettings: CreativeXCampaignSettings | undefined;
    opendcPlatformSettings: {
        campaignType: (typeof OpenDcCampaignType)[keyof typeof OpenDcCampaignType];
    } | null;
    sataliaPlatformSettings: SataliaCampaignSettings | undefined;
};

const getDefaultState = (): State => ({
    client: {} as Client,
    editableGroupValues: {},
    editableGroupValueIds: [],
    editableOverwrites: [],
    groups: [],
    id: null,
    _id: null,
    isLocked: false,
    languages: [campaignDefaultLanguage],
    masterTemplates: [],
    mastheadImageUrl: null,
    mastheadUploadConfig: null,
    name: null,
    jiraTicketUrl: null,
    dcProfileId: null,
    toneOfVoice: null,
    defaultTimelineLength: BannerDefaultDuration,
    groupFeedMappings: [],
    creativeXPlatformSettings: undefined,
    sataliaPlatformSettings: undefined,
    normalized: {
        editables: {},
        editableIds: [],
        editableGroups: {},
        editableGroupIds: [],
        editableOverwrites: {},
        masterTemplates: {},
        masterTemplateIds: [],
        overwrites: [],
        users: {},
        userIds: []
    },
    opendcPlatformSettings: null
});

export function sortEditableGroupValueIdsByValue(state: State, groupValuesIds: string[]) {
    return groupValuesIds.sort((a, b) => {
        const valueA = state.editableGroupValues[a]!.value.toUpperCase();
        const valueB = state.editableGroupValues[b]!.value.toUpperCase();

        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
    });
}

const sortByValue = (a: EditableGroupValue, b: EditableGroupValue) => {
    const valueA = a.value.toUpperCase();
    const valueB = b.value.toUpperCase();

    return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
};

export type Getters = {
    editableOverwriteValue: (
        editableId: string,
        masterTemplateId?: string,
        language?: string,
        groupValuesIds?: string[],
        editableOverwrites?: EditableWithOverwrites[]
    ) => EditableOverwrite;

    groupByGroupValue: (groupValueId: string) => (EditableGroup & { label: string }) | undefined;
    groupByGroupValueId: Record<string, string>;
    groupEditables: Editable[];
    groupValueNamesByGroup: Record<
        string,
        {
            name: string;
            editables: string[];
            editableGroupValuesNamesMap: Record<string, string>;
            editableGroupValuesNames: string[];
            editableGroupValues: string[];
        }
    >;
    masterTemplates: MasterTemplate[];
    masterTemplateLabels: Record<string, string>;
    masterTemplatesBySize: Record<string, string[]>;
    templateEditables: (masterTemplateId: string) => Editable[];
    templateById: (masterTemplateId: string) => MasterTemplate | undefined;
};

const campaignModule: Module<State, RootState> = {
    namespaced: true,

    state: getDefaultState(),

    getters: {
        editableOverwriteValue(state) {
            return (
                editableId: string,
                masterTemplateId: string | undefined = undefined,
                language: string = campaignDefaultLanguage,
                groupValuesIds: string[] = [],
                editableOverwrites: EditableWithOverwrites[] = state.editableOverwrites
            ) => {
                // 1. filter editables
                const filteredEditables = editableOverwrites.filter(
                    editableOverwrite => editableOverwrite.editable._id === editableId
                );

                if (!filteredEditables.length) {
                    return {
                        value: undefined,
                        mediaItem: null,
                        scope: null
                    };
                }

                // const permittedLanguages = [ "en" ].concat( language );
                // the line above will allow the copy for non english translation to be pre-populated with english values
                // until overwritten with proper translations - was meant to make translations easier
                const permittedLanguages = [campaignDefaultLanguage].concat(language);

                // 2. filter overrides inside editables
                const overwrites = filteredEditables.reduce<Overwrite[]>((acc, editable) => {
                    const getGroups = (override: Overwrite) =>
                        override.editableGroupValueIds.every(groupValueId => groupValuesIds.includes(groupValueId));

                    const filteredOverwrites = editable.overwrites.filter(override => {
                        // 3. Filter the editables with the languages that we can use => ["en", selectedLanguage]
                        if (!permittedLanguages.includes(override.language)) {
                            return false;
                        }

                        // 4. filter out only the scopes that have all the required values. I.e. all editableGroupValueId has been selected
                        switch (override.scope) {
                            case OverwriteScope.Campaign:
                                return true;

                            case OverwriteScope.Template:
                                return override.masterTemplateId === masterTemplateId;

                            case OverwriteScope.EditableGroup:
                                return getGroups(override);

                            case OverwriteScope.EditableGroupTemplate:
                                return override.masterTemplateId === masterTemplateId && getGroups(override);

                            default:
                                return false;
                        }
                    });

                    if (filteredOverwrites.length) {
                        acc.push(...filteredOverwrites);
                    }

                    return acc;
                }, []);

                if (!overwrites.length) {
                    return {
                        value: undefined,
                        mediaItem: null,
                        _id: null,
                        scope: null
                    };
                }

                const getScopesByOrder = () => ({ ...OverwriteScopeOrder });

                const scopesOrderByLanguage: Record<string, Record<string, number>> = {
                    [language]: getScopesByOrder()
                };

                const getOverwriteValue = (computedOverwrite: Partial<EditableOverwrite>, overwrite: Overwrite) =>
                    computedOverwrite.value || computedOverwrite.value === ""
                        ? computedOverwrite.value
                        : overwrite.value;

                const assignValueFromScope = (computedOverwrite: Partial<EditableOverwrite>) =>
                    !computedOverwrite.valueFromScope &&
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    (computedOverwrite.value || computedOverwrite.value === "" || computedOverwrite.mediaItem);

                /*
                 * If the selected language is not the default lanuage then add it to be process as for a fallback / prepopulation
                 */
                if (language !== campaignDefaultLanguage) {
                    scopesOrderByLanguage[campaignDefaultLanguage] = getScopesByOrder();
                }

                /*
                 * For each language and scope within the language find an overwrite
                 */

                const scopesByLanguage: Record<string, Record<string, Overwrite>> = {};

                Object.keys(scopesOrderByLanguage).forEach(lang => {
                    Object.keys(scopesOrderByLanguage[lang]!).forEach(scope => {
                        scopesByLanguage[lang] ??= {};

                        const or = overwrites
                            .filter(overwrite => overwrite.scope === scope && overwrite.language === lang)
                            .pop();

                        if (or) {
                            scopesByLanguage[lang][scope] = or;
                        }
                    });
                });

                const computedOverwrite = Object.values(scopesByLanguage).reduce<Partial<EditableOverwrite>>(
                    (acc, scopes) => {
                        Object.values(scopes)
                            .filter(Boolean)
                            .reverse()
                            .forEach(overwrite => {
                                acc.value = getOverwriteValue(acc, overwrite);
                                acc.mediaItem = acc.mediaItem ?? overwrite.mediaItem;
                                acc.settings = acc.settings ?? overwrite.settings;
                                acc._id = acc._id ?? overwrite._id;
                                acc.scope = acc.scope ?? overwrite.scope;
                                acc.modified = acc.modified ?? overwrite.modified;

                                if (assignValueFromScope(acc)) {
                                    acc.valueFromScope = overwrite.scope;
                                }

                                acc.settingsFromScope =
                                    acc.settingsFromScope ?? ({} as Record<OverwriteScopes, Partial<CSSSettings>>);

                                acc.settingsFromScope[overwrite.scope] = overwrite.settings!;
                            });

                        return acc;
                    },
                    {}
                );

                return computedOverwrite;
            };
        },

        groupByGroupValue(state) {
            return (groupValueId: string) =>
                state.groups.find(group => group.editableGroupValues.map(egv => egv._id).includes(groupValueId));
        },

        groupByGroupValueId(state) {
            return Object.keys(state.normalized.editableGroups).reduce<Record<string, string>>((acc, group) => {
                const { editableGroupValues } = state.normalized.editableGroups[group]!;

                editableGroupValues.forEach(groupValueId => {
                    acc[groupValueId] = group;
                });

                return acc;
            }, {});
        },

        /**
         * Get editables controlled by the groups
         * @deprecated since "other" fields had been transformed into group all editables are groupEditables
         */
        groupEditables(state) {
            const editables = dedup(
                state.groups.map(group => group.editables).reduce((acc, val) => acc.concat(val), []),
                "name"
            );

            return editables.map(grp => {
                const grpCopy = deepClone(grp);
                grpCopy.scope = EditableScope.Group;

                return grpCopy;
            });
        },

        groupValueNamesByGroup(state) {
            const populatedGroups = state.normalized.editableGroupIds.map(editableGroupId => {
                const editableGroupValuesNamesMap: Record<string, string> = {};

                const editableGroupValuesNames: string[] = [];

                state.normalized.editableGroups[editableGroupId]!.editableGroupValues.forEach(egvId => {
                    editableGroupValuesNamesMap[state.editableGroupValues[egvId]!.value] = egvId;
                    editableGroupValuesNames.push(state.editableGroupValues[egvId]!.value);
                });

                return {
                    ...state.normalized.editableGroups[editableGroupId],
                    editableGroupValuesNamesMap,
                    editableGroupValuesNames
                };
            });

            return populatedGroups.reduce<Record<string, EditableGroup>>(
                (acc, curr) => Object.assign(acc, { [curr.name!]: curr }),
                {}
            );
        },

        masterTemplates: state => {
            return state.normalized.masterTemplates;
        },

        masterTemplateLabels: state =>
            state.normalized.masterTemplateIds.reduce<Record<string, string>>((acc, masterTemplateId) => {
                const usedLabels = Object.values(acc);

                return Object.assign(acc, {
                    [masterTemplateId]: generateTemplateLabel(
                        state.normalized.masterTemplates[masterTemplateId]!,
                        usedLabels
                    )
                });
            }, {}),

        masterTemplatesBySize(state) {
            return state.masterTemplates.reduce<Record<string, string[]>>((acc, masterTemplate: MasterTemplate) => {
                const size = `${masterTemplate.width}x${masterTemplate.height}`;

                acc[size] ??= [];

                acc[size].push(masterTemplate._id);

                return acc;
            }, {});
        },

        templateEditables(state) {
            return (masterTemplateId: string) => {
                if (!masterTemplateId) {
                    return [];
                }

                return state.editableOverwrites.reduce<Editable[]>((acc, { editable }) => {
                    const defaultValues = editable.defaultValues.filter(dv => dv.masterTemplateId === masterTemplateId);

                    if (defaultValues.length) {
                        acc.push({
                            ...editable,
                            defaultValues
                        });
                    }

                    return acc;
                }, []);
            };
        },

        // todo: remove masterTemplate.id option when removing old code
        /**
         * @deprecated
         */
        templateById(state) {
            return (masterTemplateId: string) =>
                state.masterTemplates.find(masterTemplate => masterTemplate._id === masterTemplateId);
        }
    },

    mutations: {
        addEditableGroupValueToGroup(state, payload: { groupName: string; editableGroupValue: EditableGroupValue }) {
            const group = {
                ...state.normalized.editableGroups[payload.groupName]!,
                editableGroupValues: state.normalized.editableGroups[payload.groupName]!.editableGroupValues.concat(
                    payload.editableGroupValue._id
                )
            };

            state.normalized.editableGroups[group.name] = group;
        },

        clearGroups(state) {
            state.groups = [];
            state.normalized.editableGroups = {};
            state.normalized.editableGroupIds = [];
            state.normalized.editables = {};
            state.normalized.editableIds = [];
        },

        removeEditableGroupValue(state, editableGroupValueId: string) {
            state.editableGroupValueIds.splice(state.editableGroupValueIds.indexOf(editableGroupValueId), 1);
            delete state.editableGroupValues[editableGroupValueId];
        },

        removeGroupValueFromEditableGroup(state, payload: { groupName: string; editableGroupValueId: string }) {
            const group = {
                ...state.normalized.editableGroups[payload.groupName]!,
                editableGroupValues: state.normalized.editableGroups[payload.groupName]!.editableGroupValues.filter(
                    egvId => egvId !== payload.editableGroupValueId
                )
            };

            state.normalized.editableGroups[group.name] = group;
        },

        removeMasterTemplate(state, masterTemplateId: string) {
            const index = state.normalized.masterTemplateIds.indexOf(masterTemplateId);

            state.normalized.masterTemplateIds.splice(index, 1);
            delete state.normalized.masterTemplates[masterTemplateId];
        },

        setCampaign(state, payload: StoreCampaign) {
            //  todo: extend to hold all the required values;
            const campaign = deepClone(payload);

            state.id = campaign.id;
            state._id = campaign._id;
            state.languages = campaign.languages.length ? campaign.languages : [campaignDefaultLanguage];
            state.mastheadImageUrl = campaign.mastheadImageUrl;
            state.mastheadUploadConfig = campaign.mastheadUploadConfig;
            state.name = campaign.name;
            state.jiraTicketUrl = campaign.jiraTicketUrl;
            state.client = campaign.client;
            state.dcProfileId = Number(campaign.dcProfileId);
            state.isLocked = campaign.isLocked;
            state.toneOfVoice = campaign.toneOfVoice;
            state.defaultTimelineLength = campaign.defaultTimelineLength;
            state.groupFeedMappings = campaign.groupFeedMappings;
            state.creativeXPlatformSettings = campaign.creativeXPlatformSettings;
            state.opendcPlatformSettings = campaign.opendcPlatformSettings;
            state.sataliaPlatformSettings = campaign.sataliaPlatformSettings;
        },

        setCampaignUsers(state, users: User[]) {
            users.forEach(user => {
                state.normalized.users[user.id] = user;

                if (!state.normalized.userIds.includes(user.id)) {
                    state.normalized.userIds.push(user.id);
                }
            });
        },

        setDcProfileId(state, dcProfileId: string | number) {
            if (typeof dcProfileId === "string") {
                state.dcProfileId = Number(dcProfileId);
            } else {
                state.dcProfileId = dcProfileId;
            }
        },

        setEditable(state, editable: Editable) {
            state.normalized.editables[editable._id] = editable;

            if (!state.normalized.editableIds.includes(editable._id)) {
                state.normalized.editableIds.push(editable._id);
            }
        },

        setEditableGroup(state, editableGroup: EditableGroup) {
            const editableGroupValues = [...editableGroup.editableGroupValues]
                .sort(sortByValue)
                .map(editableGroupValue => editableGroupValue._id);

            const group = {
                ...editableGroup,
                editableGroupValues,
                editables: editableGroup.editables.map(editable => editable._id)
            };

            state.normalized.editableGroups[group.name] = group;

            if (!state.normalized.editableGroupIds.includes(group.name)) {
                state.normalized.editableGroupIds.push(group.name);
            }
        },

        setEditableGroupValue(state, editableGroupValue: State["editableGroupValues"][string]) {
            state.editableGroupValues[editableGroupValue._id] = editableGroupValue;

            if (!state.editableGroupValueIds.includes(editableGroupValue._id)) {
                state.editableGroupValueIds.push(editableGroupValue._id);
            }
        },

        setEditableOverwrite(state, editableOverwrite: State["editableOverwrites"][number]) {
            const editableId = editableOverwrite.editable._id;

            if (!(editableId in state.normalized.editableOverwrites)) {
                state.normalized.editableOverwrites[editableId] = {
                    editableId,
                    overwrites: []
                };
            }

            const overwrites = editableOverwrite.overwrites.map(overwrite => ({
                ...overwrite,
                editableId
            }));

            state.normalized.editableOverwrites[editableId]!.overwrites =
                state.normalized.editableOverwrites[editableId]!.overwrites.concat(overwrites);

            state.normalized.overwrites = state.normalized.overwrites.concat(overwrites);
        },

        setEditableOverwrites(state, editableOverwrites: State["editableOverwrites"]) {
            state.editableOverwrites = editableOverwrites.map(editableOverwrite => {
                const editOver = deepClone(editableOverwrite);
                editOver.editable.label = formatEditableName(editOver.editable.name);

                return editOver;
            });
        },

        setEditableRestriction(state, { editableId, isRestricted }: { editableId: string; isRestricted: boolean }) {
            const editable = state.normalized.editables[editableId];

            if (editable) {
                state.normalized.editables[editableId] = {
                    ...editable,
                    restricted: isRestricted
                };
            }
        },

        setGroups(state, groups: State["groups"]) {
            const processed = groups.map(group => {
                const grp: State["groups"][number] = deepClone(group);

                grp.label = formatGroupName(group.name);

                grp.editables = grp.editables.map(editable => ({
                    ...editable,
                    label: formatEditableName(editable.name)
                }));

                return grp;
            });

            state.groups = processed;
        },

        setInitialState(state) {
            state.client = {} as Client;
            state.editableGroupValues = {};
            state.editableGroupValueIds = [];
            state.editableOverwrites = [];
            state.groups = [];
            state.id = null;
            state._id = null;
            state.languages = [campaignDefaultLanguage];
            state.masterTemplates = [];
            state.mastheadImageUrl = null;
            state.mastheadUploadConfig = null;
            state.name = null;
            state.jiraTicketUrl = null;
            state.dcProfileId = null;

            state.normalized.editables = {};
            state.normalized.editableIds = [];
            state.normalized.editableGroups = {};
            state.normalized.editableGroupIds = [];
            state.normalized.editableOverwrites = {};
            state.normalized.masterTemplates = {};
            state.normalized.masterTemplateIds = [];
            state.normalized.overwrites = [];
            state.normalized.users = {};
            state.normalized.userIds = [];
        },

        setIsLocked(state, isLocked: State["isLocked"]) {
            state.isLocked = isLocked;
        },

        setMasterTemplate(state, masterTemplate: State["masterTemplates"][number]) {
            state.normalized.masterTemplates[masterTemplate._id] = masterTemplate;

            if (!state.normalized.masterTemplateIds.includes(masterTemplate._id)) {
                state.normalized.masterTemplateIds.push(masterTemplate._id);
            }
        },

        setMasterTemplates(state, masterTemplates: State["masterTemplates"]) {
            state.masterTemplates = masterTemplates;
        },

        sortEditableGroupValuesInGroup(state, groupName: string) {
            const existingGroup = state.normalized.editableGroups[groupName];

            if (!existingGroup) {
                return;
            }

            const editableGroupValues = sortEditableGroupValueIdsByValue(state, existingGroup.editableGroupValues);

            const group = {
                ...existingGroup,
                editableGroupValues
            };

            state.normalized.editableGroups[groupName] = group;
        }
    },

    actions: {
        addEditableGroupValue({ commit }, payload: { editableGroupValue: EditableGroupValue; groupName: string }) {
            commit(CampaignMutation.SetEditableGroupValue, payload.editableGroupValue);
            commit(CampaignMutation.AddEditableGroupValueToGroup, payload);
            commit(CampaignMutation.SortEditableGroupValuesInGroup, payload.groupName);
        },

        disassociateMasterTemplate({ commit }, masterTemplateId: string) {
            commit(CampaignMutation.RemoveMasterTemplate, masterTemplateId);
        },

        removeEditableGroupValue({ commit }, payload: { editableGroupValueId: string; groupName: string }) {
            commit(CampaignMutation.RemoveGroupValueFromEditableGroup, payload);
            commit(CampaignMutation.RemoveEditableGroupValue, payload.editableGroupValueId);
        },

        setCampaign({ commit }, campaign: StoreCampaign) {
            commit(CampaignMutation.SetCampaign, campaign);
        },

        setCampaignUsers({ commit }, users: User[]) {
            commit(CampaignMutation.SetCampaignUsers, users);
        },

        setDcProfileId({ commit }, dcProfileId: State["dcProfileId"]) {
            commit(CampaignMutation.SetDcProfileId, dcProfileId);
        },

        setEditableOverwrites({ commit }, editableOverwrites: State["editableOverwrites"]) {
            commit(CampaignMutation.SetEditableOverwrites, editableOverwrites);

            editableOverwrites.forEach(editableOverwrite => {
                commit(CampaignMutation.SetEditableOverwrite, editableOverwrite);
            });
        },

        setEditableRestriction({ commit }, payload: { editableId: string; isRestricted: boolean }) {
            commit(CampaignMutation.SetEditableRestriction, payload);
        },

        setGroups({ commit }, groups: State["groups"]) {
            commit(CampaignMutation.ClearGroups);
            commit(CampaignMutation.SetGroups, groups);

            // A single editable id can be in multiple groups so we need to collect them all
            // here and merge their defaultValues before using the mutation
            const editables = groups.reduce<Record<string, Editable>>((acc, group) => {
                group.editables.forEach(editable => {
                    if (acc[editable._id]) {
                        acc[editable._id]?.defaultValues.push(...editable.defaultValues);
                    } else {
                        // Create a new editable object - this protects the reactive object in the apollo cache from being modified
                        //  modifications to that object trigger the apollo result handler again in an infinite loop and the library crashes
                        acc[editable._id] = { ...editable, defaultValues: [...editable.defaultValues] };
                    }
                });

                return acc;
            }, {});

            Object.values(editables).forEach(editable => {
                commit(CampaignMutation.SetEditable, editable);
            });

            groups.forEach(group => {
                group.editableGroupValues.forEach(editableGroupValue => {
                    commit(CampaignMutation.SetEditableGroupValue, editableGroupValue);
                });
            });

            groups.forEach(group => {
                commit(CampaignMutation.SetEditableGroup, group);
            });
        },

        setInitialState({ commit }) {
            commit(CampaignMutation.SetInitialState);
        },

        setIsLocked({ commit }, isLocked: State["isLocked"]) {
            commit(CampaignMutation.SetIsLocked, isLocked);
        },

        setMasterTemplates(
            { commit, getters }: { commit: Commit; getters: Getters },
            masterTemplates: State["masterTemplates"]
        ) {
            // Find and remove any templates which are no longer in the list
            const currentMasterTemplates = getters.masterTemplates;
            const templatesToRemove = Object.keys(currentMasterTemplates).filter(
                id => !masterTemplates.find(mt => mt._id === id)
            );
            templatesToRemove.forEach(masterTemplateId => {
                commit(CampaignMutation.RemoveMasterTemplate, masterTemplateId);
            });

            commit(CampaignMutation.SetMasterTemplates, masterTemplates);
            masterTemplates.forEach(masterTemplate => {
                commit(CampaignMutation.SetMasterTemplate, masterTemplate);
            });
        },

        updateEditableGroupValue({ commit }, payload: { groupName: string; editableGroupValue: EditableGroupValue }) {
            commit(CampaignMutation.SetEditableGroupValue, payload.editableGroupValue);
            commit(CampaignMutation.SortEditableGroupValuesInGroup, payload.groupName);
        }
    }
};

export default campaignModule;
